/**
 * Secure Firebase Storage Adapter for SURGE Expiry Tracker
 * GDPR-Compliant with Email Hashing and Authentication
 * Users can only access their own data via hashed email
 */

class FirebaseStorageAdapter {
  constructor() {
    this.initialized = false;
    this.db = null;
    this.auth = null;
    this.customerEmail = null;
    this.hashedEmail = null;
    this.consentGiven = false;
    this.authUser = null;
  }

  /**
   * Hash email using SHA-256 for privacy
   */
  async hashEmail(email) {
    const encoder = new TextEncoder();
    const data = encoder.encode(email.toLowerCase().trim());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }

  /**
   * Initialize Firebase connection with authentication
   */
  async initialize() {
    try {
      // Initialize Firebase
      const success = await window.FirebaseConfig.initialize();
      if (!success) {
        console.error('Failed to initialize Firebase');
        return false;
      }

      this.db = window.FirebaseConfig.getDb();
      this.auth = window.FirebaseConfig.getAuth();
      this.customerEmail = window.CUSTOMER_EMAIL;

      if (!this.customerEmail) {
        console.log('No customer logged in - Firebase storage disabled');
        return false;
      }

      // Hash the customer email for privacy
      this.hashedEmail = await this.hashEmail(this.customerEmail);
      
      // Authenticate anonymously with Firebase
      // This gives us request.auth for security rules
      try {
        await this.auth.signInAnonymously();
        this.authUser = this.auth.currentUser;
        console.log('Firebase Anonymous Auth successful');
      } catch (authError) {
        console.error('Firebase Auth error:', authError);
        return false;
      }

      this.initialized = true;
      
      // Check if consent has been given
      await this.checkConsent();
      
      console.log('Firebase Storage Adapter initialized');
      console.log('Hashed Email:', this.hashedEmail.substring(0, 8) + '...'); // Show first 8 chars only
      return true;
    } catch (error) {
      console.error('Firebase initialization error:', error);
      return false;
    }
  }

  /**
   * Check if user has given GDPR consent
   */
  async checkConsent() {
    if (!this.initialized || !this.hashedEmail) return false;

    try {
      const userDoc = await this.db
        .collection('stockTracker')
        .doc(this.hashedEmail)
        .get();
        
      if (userDoc.exists) {
        const data = userDoc.data();
        this.consentGiven = data.consentGiven === true;
        return this.consentGiven;
      }
      return false;
    } catch (error) {
      console.error('Error checking consent:', error);
      return false;
    }
  }

  /**
   * Record user consent (GDPR requirement)
   */
  async recordConsent() {
    if (!this.initialized || !this.hashedEmail) return false;

    try {
      // Store ONLY the hashed email, never the plain email
      await this.db
        .collection('stockTracker')
        .doc(this.hashedEmail)
        .set({
          hashedEmail: this.hashedEmail, // Store hash only
          consentGiven: true,
          consentDate: firebase.firestore.FieldValue.serverTimestamp(),
          lastUpdated: firebase.firestore.FieldValue.serverTimestamp(),
          // NEVER store plain email in Firestore for GDPR compliance
        }, { merge: true });

      this.consentGiven = true;
      console.log('Consent recorded');
      return true;
    } catch (error) {
      console.error('Error recording consent:', error);
      return false;
    }
  }

  /**
   * Save an item to Firebase
   */
  async saveItem(item) {
    if (!this.initialized || !this.consentGiven) {
      console.error('Cannot save: not initialized or consent not given');
      return false;
    }

    try {
      const itemId = item.id.toString();
      
      // Remove any sensitive data before storing
      const sanitizedItem = { ...item };
      // Don't store plain email if it exists
      delete sanitizedItem.email;
      delete sanitizedItem.customerEmail;
      
      await this.db
        .collection('stockTracker')
        .doc(this.hashedEmail)
        .collection('items')
        .doc(itemId)
        .set({
          ...sanitizedItem,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp()
        });

      console.log('Item saved to Firebase:', itemId);
      return true;
    } catch (error) {
      console.error('Error saving item:', error);
      
      // Check if it's a permission denied error
      if (error.code === 'permission-denied') {
        console.error('PERMISSION DENIED: Check Firebase security rules');
        alert('Unable to save: Permission denied. Please contact support.');
      }
      
      return false;
    }
  }

  /**
   * Get all items for logged-in customer
   */
  async getAllItems() {
    if (!this.initialized || !this.consentGiven) {
      console.log('Cannot load items: not initialized or consent not given');
      return [];
    }

    try {
      const snapshot = await this.db
        .collection('stockTracker')
        .doc(this.hashedEmail)
        .collection('items')
        .get();

      const items = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        // Remove Firestore metadata
        delete data.updatedAt;
        items.push(data);
      });

      console.log('Loaded items from Firebase:', items.length);
      return items;
    } catch (error) {
      console.error('Error loading items:', error);
      
      if (error.code === 'permission-denied') {
        console.error('PERMISSION DENIED: Check Firebase security rules');
      }
      
      return [];
    }
  }

  /**
   * Find an item by product name
   */
  async findItemByProduct(productName) {
    if (!this.initialized || !this.consentGiven) {
      return null;
    }

    try {
      const items = await this.getAllItems();
      return items.find(item => item.productName === productName) || null;
    } catch (error) {
      console.error('Error finding item:', error);
      return null;
    }
  }

  /**
   * Delete an item
   */
  async deleteItem(itemId) {
    if (!this.initialized || !this.consentGiven) {
      console.error('Cannot delete: not initialized or consent not given');
      return false;
    }

    try {
      await this.db
        .collection('stockTracker')
        .doc(this.hashedEmail)
        .collection('items')
        .doc(itemId.toString())
        .delete();

      console.log('Item deleted from Firebase:', itemId);
      return true;
    } catch (error) {
      console.error('Error deleting item:', error);
      return false;
    }
  }

  /**
   * Delete all items for current user
   */
  async deleteAllItems() {
    if (!this.initialized || !this.consentGiven) {
      console.error('Cannot delete all: not initialized or consent not given');
      return false;
    }

    try {
      const snapshot = await this.db
        .collection('stockTracker')
        .doc(this.hashedEmail)
        .collection('items')
        .get();

      const batch = this.db.batch();
      snapshot.forEach(doc => {
        batch.delete(doc.ref);
      });
      await batch.commit();

      console.log('All items deleted from Firebase');
      return true;
    } catch (error) {
      console.error('Error deleting all items:', error);
      return false;
    }
  }

  /**
   * Delete all user data (GDPR right to erasure)
   */
  async deleteAllUserData() {
    if (!this.initialized || !this.hashedEmail) {
      console.error('Cannot delete user data: not initialized');
      return false;
    }

    try {
      // Delete all items first
      await this.deleteAllItems();
      
      // Delete the user document
      await this.db
        .collection('stockTracker')
        .doc(this.hashedEmail)
        .delete();

      console.log('All user data deleted from Firebase (GDPR erasure)');
      
      // Sign out
      await this.auth.signOut();
      
      // Reset state
      this.consentGiven = false;
      this.authUser = null;
      
      return true;
    } catch (error) {
      console.error('Error deleting user data:', error);
      return false;
    }
  }

  /**
   * Check if user is logged in
   */
  isLoggedIn() {
    return !!this.customerEmail;
  }

  /**
   * Check if user has given consent
   */
  hasConsent() {
    return this.consentGiven;
  }
  
  /**
   * Get current user's hashed email (for debugging only)
   */
  getHashedEmail() {
    return this.hashedEmail ? this.hashedEmail.substring(0, 8) + '...' : null;
  }
}

// Create global instance
window.StorageAdapter = new FirebaseStorageAdapter();

// Initialize on load
async function initializeStorage() {
  await window.StorageAdapter.initialize();
}